<template>
  <div class="action-send-documents-editor">
    <!-- Node Intro -->
    <div class="node-intro">
      <div class="node-icon">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M14 2H6C4.9 2 4.01 2.9 4.01 4L4 20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2ZM16 18H8V16H16V18ZM16 14H8V12H16V14ZM13 9V3.5L18.5 9H13Z" fill="#5587BC"/>
        </svg>
      </div>
      <p class="node-description">
        <span class="node-title">Send Document</span> will send selected documents to the patient if the Trigger event happened.
      </p>
    </div>

    <!-- Document Count Info -->
    <div class="document-count-info">
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M9 5H11V7H9V5ZM9 9H11V15H9V9ZM10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM10 18C5.59 18 2 14.41 2 10C2 5.59 5.59 2 10 2C14.41 2 18 5.59 18 10C18 14.41 14.41 18 10 18Z" fill="#5587BC"/>
      </svg>
      <span class="count-text">{{ selectedDocumentsCount }} documents selected.</span>
    </div>

    <!-- Search Input -->
    <div class="search-filter-section">
      <TextInput
        v-model="searchQuery"
        placeholder="Search Documents"
        size="medium"
      >
        <template #prefix>
          <img src="@/assets/MyPatient/search.svg" alt="Search" class="search-icon">
        </template>
      </TextInput>
    </div>

    <!-- Tab Navigation -->
    <div class="tab-navigation">
      <button
        class="tab-button"
        :class="{ active: activeTab === 'form-templates' }"
        @click="activeTab = 'form-templates'"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none" class="tab-icon">
          <path d="M12.375 0.75H3.375C2.55 0.75 1.875 1.425 1.875 2.25V12.75H3.375V2.25H12.375V0.75ZM11.625 3.75H6.375C5.55 3.75 4.8825 4.425 4.8825 5.25L4.875 15.75C4.875 16.575 5.5425 17.25 6.3675 17.25H14.625C15.45 17.25 16.125 16.575 16.125 15.75V8.25L11.625 3.75ZM6.375 15.75V5.25H10.875V9H14.625V15.75H6.375Z" :fill="activeTab === 'form-templates' ? '#004879' : '#666666'"/>
        </svg>
        <span :class="{ 'active-text': activeTab === 'form-templates' }">Form Templates</span>
      </button>
      <button
        class="tab-button"
        :class="{ active: activeTab === 'documents' }"
        @click="activeTab = 'documents'"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
          <path d="M11.625 4.5V12.375C11.625 14.0325 10.2825 15.375 8.625 15.375C6.9675 15.375 5.625 14.0325 5.625 12.375V4.5C5.625 3.465 6.465 2.625 7.5 2.625C8.535 2.625 9.375 3.465 9.375 4.5V11.625C9.375 12.0375 9.0375 12.375 8.625 12.375C8.2125 12.375 7.875 12.0375 7.875 11.625V4.5H6.75V11.625C6.75 12.66 7.59 13.5 8.625 13.5C9.66 13.5 10.5 12.66 10.5 11.625V4.5C10.5 2.8425 9.1575 1.5 7.5 1.5C5.8425 1.5 4.5 2.8425 4.5 4.5V12.375C4.5 14.655 6.345 16.5 8.625 16.5C10.905 16.5 12.75 14.655 12.75 12.375V4.5H11.625Z" :fill="activeTab === 'documents' ? '#004879' : '#1F1F1F'"/>
        </svg>
        <span :class="{ 'active-text': activeTab === 'documents' }">Documents</span>
      </button>
    </div>

    <!-- Document List or Empty State -->
    <div class="document-list-container">
      <!-- Empty State -->
      <div v-if="(activeTab === 'form-templates' && formTemplates.length === 0) || (activeTab === 'documents' && documents.filter(d => d.type === 'uploaded_file' || d.type === 'report').length === 0)" class="empty-state">
        <img src="@/assets/MyWorkBench/Workflow/document_empty.svg" alt="Empty folder" class="empty-icon" />
        <p class="empty-text">There is no file for this patient yet.</p>
      </div>

      <!-- Document List -->
      <div v-else class="document-list h-full">
        <div
          v-for="(document, index) in filteredDocuments"
          :key="document.id || index"
          class="document-item"
        >
          <Checkbox
            :modelValue="document.selected"
            size="medium"
            built_in_theme="dark"
            @update:modelValue="(value) => toggleDocumentSelection(index, value)"
          />
          <div class="document-info">
            <div class="document-name">{{ getDocumentName(document) }}</div>
            <div v-if="activeTab === 'documents'" class="document-meta">
              <span class="document-type">{{ getDocumentType(document) }}</span>
              <span class="document-timestamp">{{ formatTimestamp(document.createdAt) }}</span>
            </div>
          </div>
          <button v-if="false && activeTab === 'documents' && document.type === 'uploaded_file'" class="delete-btn" @click="deleteDocument(document)">
            <img src="@/assets/MyPatient/delete_doc.svg" alt="Delete" class="w-5 h-5" />
          </button>
        </div>
      </div>
      <!-- Pagination -->
      <div v-if="filteredDocuments.length > 0" class="pagination">
          <button class="pagination-btn" :disabled="currentPage === 1" @click="handlePrevPage">
            <svg width="8" height="12" viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7.41 1.41L6 0L0 6L6 12L7.41 10.59L2.83 6L7.41 1.41Z" :fill="currentPage === 1 ? '#C9C9C9' : '#004879'"/>
            </svg>
          </button>
          <button
            v-for="page in totalPages"
            :key="page"
            class="pagination-number"
            :class="{ active: currentPage === page }"
            @click="handlePageChange(page)"
          >
            {{ page }}
          </button>
          <button class="pagination-btn" :disabled="currentPage === totalPages" @click="handleNextPage">
            <svg width="8" height="12" viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0.59 10.59L2 12L8 6L2 0L0.59 1.41L5.17 6L0.59 10.59Z" :fill="currentPage === totalPages ? '#C9C9C9' : '#004879'"/>
            </svg>
          </button>
      </div>
    </div>

    <!-- Upload Documents Button -->
    <!-- <button class="upload-documents-btn" @click="openUploadDialog">
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12.6667 5.33333H10V0H6V5.33333H3.33333L8 10L12.6667 5.33333ZM3.33333 11.3333V13H12.6667V11.3333H3.33333Z" fill="#004879"/>
      </svg>
      Upload Documents
    </button> -->

    <!-- Save Configuration Button -->
    <SaveConfigure :disabled="!isFormValid" @save-configuration="confirmAndSave" />

    <!-- Upload Dialog -->
    <SendDocumentDialog
      :visible="showUploadDialog"
      @close="showUploadDialog = false"
      @upload="handleDocumentsUpload"
    />
  </div>
</template>

<script>
import SaveConfigure from '@/components/workflow/SaveConfigure.vue';
import SendDocumentDialog from '../Dialogs/SendDocumentDialog.vue';
import { getFormsApi } from '@/api/documents';
import { getClinicDocumentsApi, deleteClinicDocumentApi } from '@/api/patientFiles';
import { notify } from '@/utils/notify';

export default {
  name: 'ActionSendDocumentsEditor',
  components: {
    SaveConfigure,
    SendDocumentDialog
  },
  props: {
    data: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      activeTab: 'documents', // 'form-templates' or 'documents'
      searchQuery: '',
      documents: [], // Patient files from API (uploaded_file, report)
      formTemplates: [], // Form templates from API
      // Separate pagination state for each tab
      formTemplatesCurrentPage: 1,
      formTemplatesPerPage: 10,
      formTemplatesTotalCount: 0,
      documentsCurrentPage: 1,
      documentsPerPage: 10,
      documentsTotalCount: 0,
      selectedDocumentIds: new Set(), // Track selected document IDs independently
      showUploadDialog: false,
      isLoadingDocuments: false,
      clinicId: '33001', // TODO: Get from props or store
      patientId: '20001', // TODO: Get from props or store
      searchDebounceTimer: null
    };
  },
  computed: {
    // Dynamic pagination properties based on active tab
    currentPage() {
      return this.activeTab === 'form-templates' ? this.formTemplatesCurrentPage : this.documentsCurrentPage;
    },
    itemsPerPage() {
      return this.activeTab === 'form-templates' ? this.formTemplatesPerPage : this.documentsPerPage;
    },
    totalCount() {
      return this.activeTab === 'form-templates' ? this.formTemplatesTotalCount : this.documentsTotalCount;
    },
    totalPages() {
      return Math.ceil(this.totalCount / this.itemsPerPage);
    },
    selectedDocumentsCount() {
      return this.selectedDocumentIds.size;
    },
    filteredDocuments() {
      // Data is already paginated from API, just filter by tab and apply search on client side
      let filtered = [];
      
      if (this.activeTab === 'form-templates') {
        filtered = [...this.formTemplates];
      } else {
        filtered = this.documents.filter(doc => 
          doc.type === 'uploaded_file' || doc.type === 'report'
        );
      }
      
      return filtered;
    },
    isFormValid() {
      return this.selectedDocumentsCount > 0;
    }
  },
  watch: {
    'data.documents': {
      handler(newVal) {
        // When data.documents changes (e.g., loading a configured node),
        // update the selected state of documents
        if (newVal && Array.isArray(newVal)) {
          this.markSelectedDocuments(newVal);
        }
      },
      deep: true,
      immediate: true
    },
    activeTab(newTab) {
      // Reset search when switching tabs
      this.searchQuery = '';
      // Refetch data for the new tab
      if (newTab === 'form-templates') {
        this.fetchFormTemplates();
      } else {
        this.fetchPatientFiles();
      }
    },
    searchQuery() {
      // Clear existing debounce timer
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }
      
      // Debounce search to avoid excessive API calls
      this.searchDebounceTimer = setTimeout(() => {
        // Reset to page 1 when search query changes
        if (this.activeTab === 'form-templates') {
          this.formTemplatesCurrentPage = 1;
          this.fetchFormTemplates();
        } else {
          this.documentsCurrentPage = 1;
          this.fetchPatientFiles();
        }
      }, 500);
    },
    formTemplatesCurrentPage() {
      this.fetchFormTemplates();
    },
    documentsCurrentPage() {
      this.fetchPatientFiles();
    }
  },
  mounted() {
    this.loadAvailableDocuments();
  },
  beforeUnmount() {
    // Clear debounce timer on component cleanup
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = null;
    }
  },
  methods: {
    /**
     * Load available documents from patient files and forms APIs
     */
    async loadAvailableDocuments() {
      try {
        this.isLoadingDocuments = true;
        
        // Fetch patient files (uploaded files and reports)
        await this.fetchPatientFiles();
        
        // Fetch form templates
        await this.fetchFormTemplates();
        
        // Mark documents as selected based on saved configuration
        if (this.data.documents && Array.isArray(this.data.documents)) {
          this.markSelectedDocuments(this.data.documents);
        }
        
      } catch (error) {
        console.error('Error loading documents:', error);
      } finally {
        this.isLoadingDocuments = false;
      }
    },
    
    /**
     * Fetch patient files from API using clinic documents endpoint
     */
    async fetchPatientFiles() {
      try {
        const offset = (this.documentsCurrentPage - 1) * this.documentsPerPage;
        console.log(`🔍 Fetching clinic documents (limit: ${this.documentsPerPage}, offset: ${offset}, query: ${this.searchQuery})`);
        const userInfo = sessionStorage.getItem('userInfo');
        const clinicId = JSON.parse(userInfo).clinic_id;
        
        const params = {
          clinic_id: clinicId,
          limit: this.documentsPerPage,
          offset: offset,
          source: 'chart',
        };
        
        // Add search query if present
        if (this.searchQuery) {
          params.search = this.searchQuery;
        }
        
        const response = await getClinicDocumentsApi(params);
        console.log(`📁 Clinic documents response:`, response.data);
        
        // Store total count for pagination
        this.documentsTotalCount = response.data.total || 0;
        
        // Handle response structure: { items: [...], total: 6, limit: 100, offset: 0 }
        if (response.data && response.data.items && Array.isArray(response.data.items)) {
          // Process and transform the API response
          this.documents = response.data.items.map(item => ({
            id: item.id,
            clinic_id: item.clinic_id,
            patient_id: item.patient_id,
            title: item.title,
            description: item.description,
            fileName: item.file_name,
            file_name: item.file_name,
            mime_type: item.mime_type,
            contentType: item.mime_type,
            size_bytes: item.size_bytes,
            fileSize: item.size_bytes,
            download_url: item.download_url,
            external_url: item.external_url,
            externalUrl: item.external_url,
            type: item.type, // 'uploaded_file', 'returned_form'
            source: item.source, // 'chart', 'patient_upload', 'provider_upload'
            uploaded_by: item.uploaded_by,
            uploaded_by_type: item.uploaded_by_type,
            created_at: item.created_at,
            updated_at: item.updated_at,
            createdAt: item.created_at, // For timestamp display
            selected: false
          }));
          
          console.log(`✅ Processed ${this.documents.length} documents from ${response.data.count} returned`);
          
          // Restore selected state from selectedDocumentIds Set
          this.documents.forEach(doc => {
            doc.selected = this.selectedDocumentIds.has(doc.id);
          });
        } else {
          console.warn(`⚠️ Unexpected response format:`, response.data);
          this.documents = [];
        }
        
      } catch (error) {
        console.error(`❌ Error fetching clinic documents:`, error);
        this.documents = [];
      }
    },
    
    /**
     * Fetch form templates from API
     */
    async fetchFormTemplates() {
      try {
        const offset = (this.formTemplatesCurrentPage - 1) * this.formTemplatesPerPage;
        console.log(`🔍 Fetching form templates (limit: ${this.formTemplatesPerPage}, offset: ${offset})`);
        const params = {
          limit: this.formTemplatesPerPage, 
          offset: offset, 
          status: 'ACTIVE'
        };
        if (this.searchQuery) {
          params.search = this.searchQuery;
        }
        const response = await getFormsApi(params);
        console.log('📋 Form templates response:', response.data);
        
        // Store total count for pagination
        this.formTemplatesTotalCount = response.data.total || 0;
        
        // Handle response structure: { items: [...], total: 6, limit: 100, offset: 0 }
        if (response.data && response.data.items && Array.isArray(response.data.items)) {
          this.formTemplates = response.data.items.map(form => ({
            id: form.id,
            name: form.name,
            description: form.description,
            category: form.category,
            emoji_tag: form.emoji_tag,
            mapping_status: form.mapping_status,
            version: form.version,
            status: form.status,
            created_at: form.created_at,
            updated_at: form.updated_at,
            createdAt: form.created_at, // For timestamp display
            selected: false,
            type: 'form_template'
          }));
          
          console.log(`✅ Loaded ${this.formTemplates.length} form templates from ${response.data.total} total`);
          
          // Restore selected state from selectedDocumentIds Set
          this.formTemplates.forEach(form => {
            form.selected = this.selectedDocumentIds.has(form.id);
          });
        } else {
          console.warn('⚠️ Unexpected form templates response format:', response.data);
          this.formTemplates = [];
        }
      } catch (error) {
        console.error('❌ Error fetching form templates:', error);
        this.formTemplates = [];
      }
    },
    
    /**
     * Mark documents as selected based on saved document ids
     * @param {Array} savedDocuments - Array of saved documents with id and name
     */
    markSelectedDocuments(savedDocuments) {
      if (!savedDocuments || !Array.isArray(savedDocuments)) return;
      
      // Update the selectedDocumentIds Set
      this.selectedDocumentIds = new Set(savedDocuments.map(doc => doc.id));
      
      // Mark in both documents and form templates based on selectedDocumentIds
      this.documents.forEach(doc => {
        doc.selected = this.selectedDocumentIds.has(doc.id);
      });
      
      this.formTemplates.forEach(form => {
        form.selected = this.selectedDocumentIds.has(form.id);
      });
    },
    
    toggleDocumentSelection(index, isChecked) {
      const document = this.filteredDocuments[index];
      const allDocuments = this.activeTab === 'form-templates' ? this.formTemplates : this.documents;
      const actualDoc = allDocuments.find(doc => doc.id === document.id);
      if (actualDoc) {
        actualDoc.selected = isChecked;

        // Update selectedDocumentIds Set
        if (actualDoc.selected) {
          this.selectedDocumentIds.add(actualDoc.id);
        } else {
          this.selectedDocumentIds.delete(actualDoc.id);
        }
        // Don't update shared data immediately - only save when user clicks "Save Configuration"
      }
    },
    /**
     * Delete a document
     */
    async deleteDocument(document) {
      // Confirm deletion
      if (!confirm(`Are you sure you want to delete "${document.title || document.fileName}"?`)) {
        return;
      }
      
      try {
        console.log('🗑️ Deleting document:', document.id);
        
        // Call delete API
        await deleteClinicDocumentApi(document.id);
        
        // Remove from local array by finding the document by ID
        const index = this.documents.findIndex(d => d.id === document.id);
        if (index > -1) {
          this.documents.splice(index, 1);
        }
        
        notify({
          title: 'Success',
          message: 'Document deleted successfully',
          type: 'success',
          showClose: true
        });
        
        console.log('✅ Document deleted successfully');
        
        // Adjust current page if needed
        if (this.filteredDocuments.length === 0 && this.currentPage > 1) {
          this.currentPage--;
        }
        
      } catch (error) {
        if (this.documentsCurrentPage > 1) {
          this.documentsCurrentPage--;
        }
      }
    },
    handlePrevPage() {
      if (this.activeTab === 'form-templates') {
        if (this.formTemplatesCurrentPage > 1) {
          this.formTemplatesCurrentPage--;
        }
      } else {
        if (this.documentsCurrentPage > 1) {
          this.documentsCurrentPage--;
        }
      }
    },
    handleNextPage() {
      if (this.activeTab === 'form-templates') {
        if (this.formTemplatesCurrentPage < this.totalPages) {
          this.formTemplatesCurrentPage++;
        }
      } else {
        if (this.documentsCurrentPage < this.totalPages) {
          this.documentsCurrentPage++;
        }
      }
    },
    handlePageChange(page) {
      if (this.activeTab === 'form-templates') {
        this.formTemplatesCurrentPage = page;
      } else {
        this.documentsCurrentPage = page;
      }
    },
    openUploadDialog() {
      this.showUploadDialog = true;
    },
    handleDocumentsUpload(files) {
      // Add uploaded files to the document list
      const newDocuments = files.map(file => ({
        name: file.name,
        type: this.getFileType(file),
        size: file.size,
        uploadedAt: new Date().toISOString(),
        selected: false,
        file: file
      }));
      
      this.uploadedDocuments.push(...newDocuments);
      this.updateSharedData();
    },
    getFileType(file) {
      const extension = file.name.split('.').pop().toLowerCase();
      const typeMap = {
        'pdf': 'PDF',
        'doc': 'Document',
        'docx': 'Document',
        'jpg': 'Image',
        'jpeg': 'Image',
        'png': 'Image'
      };
      return typeMap[extension] || 'Document';
    },
    getDocumentName(document) {
      if (this.activeTab === 'form-templates') {
        return document.name || document.title || 'Unknown Form';
      } else if (document.type === 'uploaded_file') {
        return document.fileName || 'Unknown File';
      } else if (document.type === 'report') {
        return document.accessionId || 'Unknown Report';
      }
      return 'Unknown Document';
    },
    getDocumentType(document) {
      if (this.activeTab === 'form-templates') {
        // Show category if available, otherwise 'Form Template'
        return document.category || 'Form Template';
      } else if (document.type === 'uploaded_file') {
        return 'Uploaded File';
      } else if (document.type === 'returned_form') {
        return 'Returned Form';
      } else if (document.type === 'report') {
        return 'Report';
      }
      return 'Document';
    },
    formatTimestamp(timestamp) {
      if (!timestamp) return '';
      const date = new Date(timestamp);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    },

    confirmAndSave() {
      // Collect selected documents from both tabs with additional metadata
      const selectedFromDocuments = this.documents
        .filter(doc => doc.selected)
        .map(doc => ({
          id: doc.id,
          name: doc.file_name || doc.fileName || doc.accessionId,
          type: doc.type,
          ftype: this.getFileType(doc.mime_type || doc.contentType),
          size: this.formatFileSize(doc.size_bytes || doc.fileSize || 0),
          created: doc.created_at || doc.createdAt,
          source: 'clinic_chart'
        }));
      
      const selectedFromForms = this.formTemplates
        .filter(form => form.selected)
        .map(form => ({
          id: form.id,
          name: form.name || form.title,
          type: form.type
        }));
      
      const selectedDocuments = [...selectedFromDocuments, ...selectedFromForms];
      
      // Save without documentType and deliveryMethod fields
      this.$emit('save', {
        documents: selectedDocuments,
        selectedCount: this.selectedDocumentsCount,
        configured: this.isFormValid
      });
    },
    
    getFileType(mimeType) {
      if (!mimeType) return 'File';
      
      // Image files
      if (mimeType.startsWith('image/')) {
        if (mimeType === 'image/jpeg' || mimeType === 'image/jpg') return 'JPEG';
        if (mimeType === 'image/png') return 'PNG';
        if (mimeType === 'image/gif') return 'GIF';
        if (mimeType === 'image/bmp') return 'BMP';
        return 'Image';
      }
      
      // PDF files
      if (mimeType === 'application/pdf') return 'PDF';
      
      // Word documents
      if (mimeType === 'application/msword' || 
          mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
        return 'Document';
      }
      
      // Excel files
      if (mimeType === 'text/csv' || mimeType === 'application/csv' ||
          mimeType === 'application/vnd.ms-excel' ||
          mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
        return 'Spreadsheet';
      }
      
      // PowerPoint files
      if (mimeType === 'application/vnd.ms-powerpoint' ||
          mimeType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation') {
        return 'Presentation';
      }
      
      // Archive files
      if (mimeType === 'application/zip' ||
          mimeType === 'application/x-zip-compressed' ||
          mimeType === 'application/x-rar-compressed' ||
          mimeType === 'application/x-7z-compressed') {
        return 'Archive';
      }
      
      // Text files
      if (mimeType === 'text/plain') return 'Text';
      
      return 'File';
    },
    
    formatFileSize(bytes) {
      if (bytes === 0) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    }
  }
};
</script>

<style scoped>
.action-send-documents-editor {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

/* Node Intro */
.node-intro {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 8px 16px;
  background: #E4F3F5;
  border-radius: 5px;
}

.node-icon {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.node-description {
  flex: 1;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 300;
  line-height: 18px;
  color: #5587BC;
  margin: 0;
}

.node-title {
  font-weight: 700;
}

/* Document Count Info */
.document-count-info {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #E4F3F5;
  border-radius: 4px;
}

.count-text {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #5587BC;
}

/* Search and Filters */
.search-filter-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.search-icon {
  width: 16px;
  height: 16px;
  opacity: 0.5;
}

/* Tab Navigation */
.tab-navigation {
  display: flex;
  border-bottom: 1px solid #E5E7EB;
  background: #FFFFFF;
  gap: 0;
  margin-bottom: 8px;
}

.tab-button {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 8px 8px;
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #999999;
}

.tab-button.active {
  border-bottom-color: #004879;
}

.tab-button span {
  transition: color 0.2s ease;
}

.tab-button span.active-text {
  color: #004879;
  font-weight: 500;
}

.tab-button:hover:not(.active) {
  background: #F9FAFB;
}



/* Document List Container */
.document-list-container {
  min-height: 200px;
  border-top: 1px dashed #C9C9C9;
  border-bottom: 1px dashed #C9C9C9;
  padding: 2px 6px;
  background: #FFF;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 16px;
}

.empty-icon {
  width: 80px;
  height: 80px;
  margin-bottom: 16px;
}

.empty-text {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #999;
  text-align: center;
  margin: 0;
}

/* Document List */
.document-list {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.document-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
}

.document-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.document-name {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.document-meta {
  display: flex;
  gap: 8px;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  color: #999;
}

.delete-btn {
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  cursor: pointer;
  border-radius: 4px;
  transition: background 0.2s ease;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.delete-btn:hover {
  background: #F3F4F6;
}

/* Pagination */
.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 16px;
  margin-bottom: 16px;
}

.pagination-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  border-radius: 4px;
  transition: background 0.2s ease;
}

.pagination-btn:hover:not(:disabled) {
  background: #F3F4F6;
}

.pagination-btn:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.pagination-number {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #666;
  background: transparent;
  border: none;
  cursor: pointer;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.pagination-number:hover {
  background: #F3F4F6;
}

.pagination-number.active {
  background: #004879;
  color: #FFFFFF;
}

/* Upload Documents Button */
.upload-documents-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 32px;
  font-family: 'IBM Plex Sans Hebrew', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #004879;
  background: #FFFFFF;
  border: 1px solid #004879;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.upload-documents-btn:hover {
  background: #F0F7FF;
}
</style>
